import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, Check, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Field } from "../components/ui";
import Logo from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { claimPendingReferral } from "./ReferralLanding";
import useSeo from "../lib/useSeo";
import { useTranslation } from "react-i18next";
import LegalLinks from "../components/LegalLinks";

/**
 * Dedicated signup, split-screen with the value proposition beside the form.
 *
 * Registration hands straight into the authenticator setup the backend requires,
 * so this owns the whole flow rather than bouncing between pages: account → scan QR
 * → verify → in.
 */
/** Timezone is the cheapest honest hint the browser offers, and it separates the
 *  three DACH countries cleanly. It only pre-selects — the user confirms, and the
 *  declared country is what the tax and invoicing rules key on. */
function guessCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return { "Europe/Berlin": "DE", "Europe/Busingen": "DE",
             "Europe/Vienna": "AT", "Europe/Zurich": "CH" }[tz] || "DE";
  } catch { return "DE"; }
}

export default function Register() {
  const { t, i18n } = useTranslation();
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", country: guessCountry() });
  const [countries, setCountries] = useState([]);
  const [setup, setSetup] = useState(null);   // { setup_token, qr, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({ title: t("register.title"), description: t("register.seoDesc"), path: "/register" });

  // The list comes from the backend so the VAT rate quoted at sign-up is the same
  // one the invoice will charge — a duplicated frontend constant would drift.
  useEffect(() => {
    api.countries().then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (form.password.length < 8) { toast.error(t("register.passwordShort")); return; }
    setBusy(true);
    try {
      const { data } = await api.register(form);
      setSetup(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.verifyTotpSetup({ token: setup.setup_token, code });
      completeLogin(data.user);
      // A referral stashed before signup is claimed the moment there's an account.
      claimPendingReferral();
      navigate("/", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const benefits = ["b1", "b2", "b3", "b4"];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Value side — kept visible so the form never feels like a toll gate. */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-brand-600 via-brand-600 to-indigo-700 text-white p-12">
        <Link to="/"><Logo size={34} tone="light" /></Link>
        <div>
          <h2 className="text-4xl font-heading font-semibold tracking-tighter leading-tight">
            {t("register.pitchTitle")}
          </h2>
          <ul className="mt-7 space-y-3.5">
            {benefits.map((k) => (
              <li key={k} className="flex items-start gap-3 text-white/85">
                <Check size={17} className="shrink-0 mt-0.5" /> {t(`register.${k}`)}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-white/60">{t("register.footNote")}</p>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden block mb-8"><Logo size={34} /></Link>

          {!setup ? (
            <>
              <h1 className="text-3xl font-heading font-semibold tracking-tighter">{t("register.title")}</h1>
              <p className="text-slate-500 mt-2 text-sm">{t("register.sub")}</p>

              <div className="mt-7 space-y-4">
                <Field label={t("auth.name")}>
                  <input className="input" data-testid="reg-name" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         placeholder={t("register.namePlaceholder")} />
                </Field>
                <Field label={t("auth.email")}>
                  <input className="input" type="email" data-testid="reg-email" value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         autoComplete="email" />
                </Field>
                <Field label={t("register.country")} hint={t("register.countryHint")}>
                  <select className="input" data-testid="reg-country" value={form.country}
                          onChange={(e) => setForm({ ...form, country: e.target.value })}>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {i18n.language === "de" ? c.name_de : c.name_en}
                        {c.code !== "XX" ? ` · ${c.currency}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t("auth.password")}>
                  <input className="input" type="password" data-testid="reg-password" value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         onKeyDown={(e) => e.key === "Enter" && submit()}
                         autoComplete="new-password" />
                  <p className="text-[11px] text-slate-400 mt-1.5">{t("register.passwordHint")}</p>
                </Field>
                <button className="btn-primary w-full justify-center py-3" onClick={submit} disabled={busy}
                        data-testid="reg-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("register.cta")} <ArrowRight size={16} />
                </button>
              </div>

              <p className="text-sm text-slate-500 mt-6 text-center">
                {t("auth.haveAccount")}{" "}
                <Link to="/login" className="text-brand-600 font-medium hover:underline">{t("auth.signIn")}</Link>
              </p>
              <p className="text-[11px] text-slate-400 mt-6 text-center leading-relaxed">
                {t("register.legal")}{" "}
                <Link to="/terms" className="underline">{t("legal.terms")}</Link>{" "}
                {t("register.and")}{" "}
                <Link to="/privacy" className="underline">{t("legal.privacy")}</Link>.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 pill bg-brand-50 text-brand-700">
                <ShieldCheck size={13} /> {t("register.stepTwo")}
              </div>
              <h1 className="text-3xl font-heading font-semibold tracking-tighter mt-3">{t("auth.setupTitle")}</h1>
              <p className="text-slate-500 mt-2 text-sm leading-relaxed">{t("auth.setupSub")}</p>

              <div className="mt-6 flex justify-center">
                <img src={setup.qr} alt="Authenticator QR code" width={190} height={190}
                     className="rounded-xl border border-slate-200" data-testid="reg-qr" />
              </div>
              <details className="mt-3 text-center">
                <summary className="text-xs text-slate-400 cursor-pointer">{t("auth.cantScan")}</summary>
                <code className="block mt-2 text-xs tabular bg-slate-50 rounded-lg p-2.5 break-all">{setup.secret}</code>
              </details>

              <div className="mt-6">
                <Field label={t("auth.enterCode")}>
                  <input className="input tabular text-center text-lg tracking-[0.3em]" data-testid="reg-code"
                         value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                         onKeyDown={(e) => e.key === "Enter" && verify()}
                         inputMode="numeric" autoComplete="one-time-code" placeholder="000000" />
                </Field>
                <button className="btn-primary w-full justify-center py-3 mt-4" onClick={verify}
                        disabled={busy || code.length !== 6} data-testid="reg-verify">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null} {t("auth.verify")}
                </button>
              </div>
            </>
          )}
          <LegalLinks className="mt-10 text-xs text-slate-400" />
        </div>
      </div>
    </div>
  );
}
